import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Button, Input, showToast } from "@vv/ui";
import { useEffect, useState } from "react";
import { requestsApi } from "../api/requests.api";
import { ALL_ATTACHMENT_CATEGORIES, type AttachmentCategory } from "../domain/request.types";
import { useRequestsDispatch, useRequestsSelector } from "../state/hooks";
import { fetchAttachments, uploadAttachment } from "../state/requests.thunks";

export interface AttachmentsSectionProps {
  requestId: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentsSection({ requestId }: AttachmentsSectionProps) {
  const dispatch = useRequestsDispatch();
  const attachments = useRequestsSelector((state) => state.requests.attachments[requestId]) ?? [];
  const [category, setCategory] = useState<AttachmentCategory>("Evidence");
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchAttachments(requestId));
  }, [dispatch, requestId]);

  async function handleFileSelected(fileList: FileList | null) {
    const file = fileList?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await dispatch(uploadAttachment({ requestId, file, category })).unwrap();
      showToast("Attachment uploaded", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not upload attachment", "error");
    } finally {
      setUploading(false);
    }
  }

  async function handleDownload(attachmentId: string) {
    setDownloadingId(attachmentId);
    try {
      const { url } = await requestsApi.getAttachmentDownloadUrl(requestId, attachmentId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not get download link", "error");
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <Stack spacing={2} sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Attachments
      </Typography>

      {attachments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No files attached yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {attachments.map((attachment) => (
            <Stack
              key={attachment.id}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ py: 0.5 }}
            >
              <Chip label={attachment.category} size="small" />
              <Typography variant="body2" sx={{ flexGrow: 1, overflowWrap: "anywhere" }}>
                {attachment.originalFilename}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatBytes(attachment.sizeBytes)}
              </Typography>
              <IconButton
                size="small"
                aria-label={`download ${attachment.originalFilename}`}
                disabled={downloadingId === attachment.id}
                onClick={() => handleDownload(attachment.id)}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Input
          select
          label="Category"
          size="small"
          value={category}
          onChange={(event) => setCategory(event.target.value as AttachmentCategory)}
          sx={{ minWidth: 160 }}
        >
          {ALL_ATTACHMENT_CATEGORIES.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Input>
        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          loading={uploading}
        >
          Upload file
          <input
            type="file"
            hidden
            onChange={(event) => {
              void handleFileSelected(event.target.files);
              event.target.value = "";
            }}
          />
        </Button>
      </Stack>
    </Stack>
  );
}
